import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { User } from '../interfaces/user.model';
import { Router } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StorageService } from '../../services/storage.service';
import { IRegisterReq, IRegisterResult } from '../interfaces/register.interface';
import { IUserLoginSuccess } from '../interfaces/login.interface';
import { JwtHelperService } from '@auth0/angular-jwt';
import { IUser } from 'src/app/core/interfaces/core.interfaces'
import { USER_TYPES } from 'src/app/core/constants';
import { IResponse } from '../../interfaces/response.interface';
import { GleapService } from '../../services/gleap.service';
import { Gleap } from 'capacitor-gleap-plugin';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { AppInfo } from '@capacitor/app';
import { DeviceDetectionService } from '../../services/device-detection.service';
import { NotificationsService } from '../../services/notifications.service';
import { logSessionEvent } from '../session-log';
const baseUrl = `${environment.apiUrl}auth`;

const PROFILE_OVERLAY_STORAGE_KEY = 'profileOverlay';
const XSRF_TOKEN_STORAGE_KEY = 'xsrfToken';
// Whitelist of MIS-DATOS-editable fields. Claims that are never edited from
// MIS DATOS (role, type, id, exp, iat, ...) can NEVER be shadowed by the overlay.
const PROFILE_OVERLAY_KEYS = [
	'firstName', 'lastName', 'companyName', 'email',
	'identification', 'birthDate', 'address', 'postalCode', 'zoneId'
] as const;
type ProfileOverlayKey = typeof PROFILE_OVERLAY_KEYS[number];
type ProfileOverlayFields = Partial<Pick<IUser, ProfileOverlayKey>>;

interface IProfileOverlay {
	userId: string;
	fields: ProfileOverlayFields;
	// Claim values captured at write time, used to invalidate stale overlay
	// keys on a later token refresh.
	baseline: ProfileOverlayFields;
}

// Thrown by `performRefresh()` when the session epoch changed between the
// refresh being initiated and its response arriving — i.e. the session that
// requested the refresh no longer exists (logged out / replaced). Callers
// (the 401 interceptor, the boot initializer) already treat any refresh
// error as "don't retry, don't resurrect" — see design D5.
export class SessionSupersededError extends Error {}

@Injectable({ providedIn: 'root' })
export class AuthService {
	private userSubject = new BehaviorSubject<User | null>(null);
	public user = this.userSubject.asObservable();
	private isLoggingOut = false;
	// Single in-flight refresh, shared across every caller (401 interceptor AND
	// the bootstrap proactive refresh). `refCount: false` keeps the HTTP call
	// alive even if every subscriber unsubscribes (e.g. a bootstrap timeout) —
	// with a rotating refresh token, aborting mid-flight means the server
	// rotated and the client never stored the replacement.
	private refreshInFlight$: Observable<string> | null = null;
	// Bumped on every logout/session clear. A refresh started in a previous
	// epoch that resolves AFTER that clear must not resurrect a token into the
	// now-cleared session (bootstrap can time out without aborting the HTTP
	// call — see refCount:false above — so a late 200 is a real scenario).
	private _sessionEpoch = 0;
	// Identity-bound overlay of MIS-DATOS-edited fields, layered on top of the
	// decoded JWT claims so an edit reflects immediately without a relogin.
	private profileOverlay: IProfileOverlay | null = null;
	private overlayHydrated = false;

	constructor(
		private router: Router,
		private deviceDetectionService: DeviceDetectionService,
		private http: HttpClient,
		private readonly jwtHelper: JwtHelperService,
		private storageService: StorageService,
		private gleapService: GleapService,
		private notificationsService: NotificationsService
	) { }

	public get userValue(): User | null {
		return this.userSubject.value;
	}

	// Read-only accessor for the session epoch. Consumers (error interceptor)
	// capture this at request-dispatch time to detect whether the session
	// that dispatched a request is still the active one by the time a
	// response/error for it arrives (see design D3/D4).
	public get sessionEpoch(): number {
		return this._sessionEpoch;
	}

	public get userName(): string | undefined {
		const user = this.currentUserFromToken;
		return (user.type == 'person') ? user.firstName : user.companyName

	}

	public get currentUserFromToken(): IUser {
		const token = this.token;
		const claims = token ? this.decodeUserFromToken(token) : {} as IUser;
		const overlay = this.readProfileOverlay();
		if (overlay && overlay.userId === claims.id) {
			return { ...claims, ...overlay.fields };
		}
		return claims;
	}

	// Takes the FULL GET users/me DTO (not a partial patch); filtering against
	// the whitelist and the current claims lives here so callers stay dumb.
	public applyProfileUpdate(user: Partial<IUser> | null | undefined): void {
		const token = this.token;
		const claims = token ? this.decodeUserFromToken(token) : {} as IUser;
		if (!user || !claims.id) {
			this.clearProfileOverlay();
			return;
		}
		const fields: ProfileOverlayFields = {};
		const baseline: ProfileOverlayFields = {};
		for (const key of PROFILE_OVERLAY_KEYS) {
			const newValue = user[key];
			if (newValue === undefined || newValue === null || newValue === claims[key]) {
				continue;
			}
			(fields as any)[key] = newValue;
			(baseline as any)[key] = claims[key];
		}
		if (Object.keys(fields).length === 0) {
			this.clearProfileOverlay();
			return;
		}
		this.writeProfileOverlay({ userId: claims.id, fields, baseline });
	}

	public get token(): string | null {
		return this.storageService.get('token');
	}

	// Synchronous getter, mirroring `get token()` above. No Observable: the
	// only consumers (JwtInterceptor.intercept, ErrorInterceptor.addToken) are
	// synchronous by construction and there are zero reactive consumers (see
	// design D2). Reads through storage on every call so it is automatically
	// correct across both teardown paths (beginNewSession / clearLocalSession),
	// which wipe via a raw `localStorage.clear()` with no in-memory mirror to
	// forget to reset.
	public get xsrfToken(): string | null {
		return this.storageService.get(XSRF_TOKEN_STORAGE_KEY);
	}

	public get isLoggedIn(): boolean {
		const token = this.token;
		if (!token) {
			return false;
		}
		try {
			return !this.jwtHelper.isTokenExpired(token);
		} catch (error) {
			console.error('Corrupt token in storage, clearing it', error);
			this.storageService.remove('token');
			return false;
		}
	}

	// Existence-only check, deliberately NOT gated on expiry. Interceptors must
	// keep attaching the header and attempting refresh on an expired-but-present
	// token — `isLoggedIn` (existence + validity) is the wrong gate for that.
	public get hasToken(): boolean {
		return !!this.token;
	}

	private decodeUserFromToken(token: string): IUser {
		try {
			return this.jwtHelper.decodeToken(token) ?? {} as IUser;
		} catch (error) {
			console.error('Corrupt token in storage, clearing it', error);
			this.storageService.remove('token');
			return {} as IUser;
		}
	}

	private isValidJwtShape(token: string): boolean {
		return token.split('.').length === 3;
	}

	// Lazy-hydrated in-memory cache over StorageService, avoiding a JSON.parse
	// on every currentUserFromToken read (called per page enter).
	private readProfileOverlay(): IProfileOverlay | null {
		if (this.overlayHydrated) {
			return this.profileOverlay;
		}
		this.overlayHydrated = true;
		const raw = this.storageService.get(PROFILE_OVERLAY_STORAGE_KEY);
		if (!raw) {
			this.profileOverlay = null;
			return null;
		}
		try {
			this.profileOverlay = JSON.parse(raw);
		} catch (error) {
			console.error('Corrupt profile overlay in storage, clearing it', error);
			this.storageService.remove(PROFILE_OVERLAY_STORAGE_KEY);
			this.profileOverlay = null;
		}
		return this.profileOverlay;
	}

	private writeProfileOverlay(overlay: IProfileOverlay | null): void {
		this.profileOverlay = overlay;
		this.overlayHydrated = true;
		if (overlay) {
			this.storageService.set(PROFILE_OVERLAY_STORAGE_KEY, JSON.stringify(overlay));
		} else {
			this.storageService.remove(PROFILE_OVERLAY_STORAGE_KEY);
		}
	}

	private clearProfileOverlay(): void {
		this.writeProfileOverlay(null);
	}

	// In cookie mode the backend stops shipping the token in the JSON body (it
	// lives in an HttpOnly cookie instead) but still exposes it via a readable
	// `Authorization` response header so the client can decode user claims locally.
	private extractAuthTokens(response: HttpResponse<IUserLoginSuccess>): { token: string; refreshToken?: string; xsrfToken?: string } {
		const body = response.body?.data;
		const token = body?.token || (environment.cookieAuth ? this.extractBearerToken(response.headers.get('Authorization')) : '');
		return { token, refreshToken: body?.refreshToken, xsrfToken: body?.xsrfToken };
	}

	private extractBearerToken(header: string | null): string {
		if (!header) {
			return '';
		}
		const match = header.match(/^Bearer\s+(.+)$/i);
		return match ? match[1] : header;
	}

	// Intentional teardown for an identity transition. Called ONLY after sign-in
	// returns 200, so a failed login can never destroy a live session.
	// The breadcrumb is emitted BEFORE the bump on purpose: `epoch` names the
	// session being RETIRED (the new one is always epoch + 1), and it still gets
	// out if `clear()` throws. Unlike `clearLocalSession()` this does NOT emit
	// `userSubject.next(null)` nor navigate — the caller is mid-login.
	private beginNewSession(): void {
		logSessionEvent({
			reason: 'auth.login.session-reset',
			at: new Date().toISOString(),
			epoch: this._sessionEpoch,
		});
		this._sessionEpoch++;          // supersedes any in-flight refresh (see performRefresh)
		this.refreshInFlight$ = null;
		this.clearProfileOverlay();    // resets the in-memory overlay cache, not just storage
		localStorage.clear();
		sessionStorage.clear();
	}

	login(email: string, password: string): Observable<IUser> {
		return this.http.post<IUserLoginSuccess>(`${baseUrl}/sign-in`, { email, password }, { observe: 'response' })
			.pipe(
				map(response => {
					this.beginNewSession();
					const { token, refreshToken, xsrfToken } = this.extractAuthTokens(response);
					this.setToken(token);
					this.setRefreshToken(refreshToken);
					this.setXsrfToken(xsrfToken, 'sign-in');
					const user = this.decodeUserFromToken(token);
					const device = this.deviceDetectionService.getDeviceInfo();
					Gleap.identify({
						userId: user.id ?? '',
						name: (user.companyName) ? user.companyName : `${user.firstName} ${user.lastName}`,
						email: user.email,
						companyName: user.companyName,
						...device
						
					});
					this.userSubject.next(user);
					return user;
				}),
				tap(user => this.identifyOnCapgo(user.id ?? '')),
				tap(() => this.triggerPushRegistration())
			);
	}

	// Fire-and-forget: push registration is additive to email notifications
	// (spec: "Silent Failure on Registration Error") and must never delay or
	// break login. NotificationsService.initPushNotifications() already
	// swallows its own setup errors, but a caller-side catch guards against
	// any unexpected rejection reaching an unhandled promise.
	private triggerPushRegistration(): void {
		void this.notificationsService.initPushNotifications().catch(error => {
			console.error('Error initializing push notifications after login:', error);
		});
	}

	private identifyOnCapgo(userId: string): void {
		if (!Capacitor.isNativePlatform() || !userId) return;
		CapacitorUpdater.setCustomId({ customId: userId }).catch(console.error);
		CapacitorUpdater.current().then(({ bundle, native }) => {
			Gleap.attachCustomData({
				data: {
					capgoBundle: bundle.version,
					capgoNative: native,
				}
			});
		}).catch(console.error);
	}

	register(user: IRegisterReq): Observable<IRegisterResult> {
		return this.http.post<IResponse>(`${baseUrl}/sign-up`, { ...user })
			.pipe(map(response => response.data));
	}

	validateAccount(code: string): Observable<any> {
		return this.http.get<any>(`${baseUrl}/validate?code=${code}`)
			.pipe(map(response => response.data));
	}

	resetPassword(email: string): Observable<any> {
		return this.http.post<any>(`${baseUrl}/password-reset/send-email`, { email })
			.pipe(map(response => response.data));
	}

	recoverPassword(code: string, password: string, passwordConfirm: string, email: string): Observable<any> {
		return this.http.post<any>(`${baseUrl}/recover`, {
			code,
			password,
			password_confirm: passwordConfirm,
			email
		}).pipe(map(response => response.data));
	}

	setToken(token: string): void {
		if (!token || !this.isValidJwtShape(token)) {
			console.error('Refusing to store malformed token', token);
			return;
		}
		this.storageService.set('token', token);
	}

	setRefreshToken(refreshToken?: string): void {
		if (refreshToken) {
			this.storageService.set('refreshToken', refreshToken);
		}
	}

	// Loud, non-fatal. Deliberately does NOT throw (a missing field must never
	// look like a refresh failure -> mass logout, design D5) and does NOT clear
	// a previously stored value on omission (clearing guarantees a 403 on the
	// next mutating request; keeping the last-known-good value at worst fails
	// identically, and succeeds if the backend didn't actually rotate it).
	private setXsrfToken(xsrfToken: string | undefined, flow: 'sign-in' | 'token-refresh'): void {
		if (xsrfToken) {
			this.storageService.set(XSRF_TOKEN_STORAGE_KEY, xsrfToken);
			return;
		}
		if (!environment.cookieAuth) {
			return; // non-cookie backends legitimately omit it
		}
		logSessionEvent({
			reason: 'auth.xsrf.missing',
			at: new Date().toISOString(),
			epoch: this._sessionEpoch,
			path: `/auth/${flow}`
		});
	}

	refreshToken(): Observable<string> {
		if (this.refreshInFlight$) {
			return this.refreshInFlight$;
		}

		const epoch = this._sessionEpoch;
		this.refreshInFlight$ = this.performRefresh(epoch).pipe(
			// MUST run BEFORE shareReplay: here it fires once, when the shared HTTP
			// call itself settles. After shareReplay it would fire once per
			// subscriber instead, and the in-flight slot would never free up.
			finalize(() => { this.refreshInFlight$ = null; }),
			shareReplay({ bufferSize: 1, refCount: false })
		);
		return this.refreshInFlight$;
	}

	private performRefresh(epoch: number): Observable<string> {
		const refreshToken = this.storageService.get('refreshToken');
		return this.http.post<IUserLoginSuccess>(`${baseUrl}/token-refresh`, { refreshToken }, { observe: 'response' })
			.pipe(
				map(response => {
					// The session that requested this refresh no longer exists by the
					// time the response arrived (logout/replace happened mid-flight).
					// Handing out this token would resurrect a dead session — throw
					// instead of returning it, and never persist it (design D5).
					if (epoch !== this._sessionEpoch) {
						throw new SessionSupersededError();
					}
					const { token, refreshToken: newRefreshToken, xsrfToken } = this.extractAuthTokens(response);
					this.setToken(token);
					this.setRefreshToken(newRefreshToken);
					this.setXsrfToken(xsrfToken, 'token-refresh');
					return token;
				})
			);
	}

	logout(): Observable<void> {
		// Reentrancy guard: an in-flight logout may itself trigger a 401
		// (e.g. an already-expired token), which would make ErrorInterceptor
		// call logout() again. Skip the duplicate backend call — the local
		// cleanup below will still run once, in the finalize of the first call.
		// The caller still gets a resolved observable so awaiting logout()
		// never hangs regardless of which caller "won" the race.
		if (this.isLoggingOut) {
			return of(void 0);
		}
		this.isLoggingOut = true;

		const flow$ = this.http.get(`${baseUrl}/logout`)
			.pipe(
				// Best-effort server-side revocation: never block local cleanup on it.
				catchError(() => of(null)),
				finalize(() => {
					this.clearLocalSession();
					this.isLoggingOut = false;
				}),
				map(() => void 0),
				// Hot + replayed: the side effect is already in motion via the
				// subscribe() below, so a caller awaiting the returned observable
				// (e.g. the bootstrap initializer, PR3) gets the settled value
				// instead of triggering a second execution (cold Observable
				// would double the HTTP call and the local cleanup).
				shareReplay({ bufferSize: 1, refCount: false })
			);
		flow$.subscribe();
		return flow$;
	}

	private clearLocalSession(): void {
		this._sessionEpoch++;
		this.refreshInFlight$ = null;
		this.clearProfileOverlay();
		localStorage.clear();
		this.userSubject.next(null);
		// Limpiar sessionStorage también para asegurar que no queden datos en memoria
		sessionStorage.clear();
		this.router.navigateByUrl('/auth/login');
	}
}